import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Flex } from "antd";

import { Modal } from "@av/components/atoms/Modal";
import { Typography } from "@av/components/atoms/typography/Typography";
import { DELETE_MODAL_WIDTH } from "../constants";
import { useCompanyKnowledgeStyles } from "../styles";

import type { CompanyKnowledgeDocument } from "../types";

type DeleteDocumentModalProps = {
  document?: CompanyKnowledgeDocument;
  isDeleting?: boolean;
  onCancel: () => void;
  onDelete: (document: CompanyKnowledgeDocument) => Promise<void>;
};

export function DeleteDocumentModal({
  document,
  isDeleting,
  onCancel,
  onDelete,
}: DeleteDocumentModalProps) {
  const { t } = useTranslation();
  const { styles } = useCompanyKnowledgeStyles();
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const title = t("platform.adminSettings.companyKnowledge_deleteTitle");

  const handleCancel = () => {
    setDeleteError(undefined);
    onCancel();
  };

  const handleDelete = async () => {
    if (!document) {
      return;
    }

    setDeleteError(undefined);

    try {
      await onDelete(document);
    } catch {
      setDeleteError(t("platform.adminSettings.companyKnowledge_deleteFailed"));
    }
  };

  return (
    <Modal
      aria-label={title}
      className={styles.deleteModal}
      closable={false}
      cancelButtonProps={{ className: styles.deleteModalCancelButton }}
      cancelText={t("platform.adminSettings.companyKnowledge_cancel")}
      confirmLoading={isDeleting}
      okButtonProps={{ className: styles.deleteModalDeleteButton }}
      okText={t("platform.adminSettings.companyKnowledge_delete")}
      onCancel={handleCancel}
      onOk={handleDelete}
      open={!!document}
      testIdPrefix="company-knowledge-delete-document"
      title={
        <Flex align="center" gap={12}>
          <ExclamationCircleOutlined aria-hidden className={styles.deleteModalIcon} />
          <Typography.Title
            className={styles.deleteModalTitle}
            level={3}
            testIdPrefix="company-knowledge-delete-document-title"
          >
            {title}
          </Typography.Title>
        </Flex>
      }
      width={DELETE_MODAL_WIDTH}
    >
      <div className={styles.deleteModalBody}>
        <Typography.Paragraph
          className={styles.deleteModalParagraph}
          testIdPrefix="company-knowledge-delete-document-impact-intro"
        >
          {t("platform.adminSettings.companyKnowledge_deleteImpactIntro")}
        </Typography.Paragraph>
        <ul className={styles.deleteImpactList}>
          <li>{t("platform.adminSettings.companyKnowledge_deleteImpactEmployeeAccess")}</li>
          <li>{t("platform.adminSettings.companyKnowledge_deleteImpactAvaAnswers")}</li>
        </ul>
        <Typography.Paragraph
          className={styles.deleteModalParagraph}
          testIdPrefix="company-knowledge-delete-document-confirm-question"
        >
          {t("platform.adminSettings.companyKnowledge_deleteConfirmQuestion")}
        </Typography.Paragraph>
        {deleteError && (
          <Typography.Paragraph
            className={styles.deleteModalError}
            testIdPrefix="company-knowledge-delete-document-error"
            type="danger"
          >
            {deleteError}
          </Typography.Paragraph>
        )}
      </div>
    </Modal>
  );
}
